import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { PublicClientApplication } from '@azure/msal-browser'
import { config } from '../config/environment'
import { authApiService } from '../services/authApiService'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../services/BaseApiService'
import { OAUTH_LINK_KEY } from './SignInMethods'

export default function AuthRedirectPage() {
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const handled = useRef(false)

  useEffect(() => {
    if (handled.current) return
    handled.current = true

    // const log = (msg: string) => console.log('[MSAL]', msg)
    // const err = (msg: string, e?: unknown) => console.error('[MSAL]', msg, e)

    // log(`href: ${window.location.href}`)
    // log(`hash: ${window.location.hash}`)
    // log(`search: ${window.location.search}`)

    if (!config.microsoftClientId) { navigate('/login'); return }

    const msal = new PublicClientApplication({
      auth: {
        clientId: config.microsoftClientId,
        redirectUri: window.location.origin + '/auth-redirect',
      },
    })

    const nav = navigate
    const ref = refresh

    // Set by Settings > Security when the redirect is to link an account rather than to sign in.
    const linkMode = sessionStorage.getItem(OAUTH_LINK_KEY) === 'microsoft'
    sessionStorage.removeItem(OAUTH_LINK_KEY)
    const backToSettings = (message: string) =>
      nav('/app/settings?tab=mfa', { replace: true, state: { signInMethodNotice: { ok: false, message } } })

    msal.initialize().then(async () => {
      // log('initialized')
      try {
        const result = await msal.handleRedirectPromise({ navigateToLoginRequestUrl: false })
        // log(`handleRedirectPromise: account=${result?.account?.username ?? 'null'} idToken=${!!result?.idToken}`)
        if (!result?.account) {
          // log('no account in result, navigating to /login')
          if (linkMode) { backToSettings('The Microsoft sign-in was cancelled.'); return }
          nav('/login')
          return
        }
        msal.setActiveAccount(result.account)
        let idToken = result.idToken
        if (!idToken) {
          // log('idToken empty, trying acquireTokenSilent')
          const silent = await msal.acquireTokenSilent({
            scopes: ['openid', 'profile', 'email'],
            account: result.account,
          })
          idToken = silent.idToken
          // log(`acquireTokenSilent: idToken=${!!idToken}`)
        }
        if (!idToken) {
          // err('no idToken after silent')
          if (linkMode) { backToSettings('Microsoft returned no sign-in token.'); return }
          nav('/login?error=microsoft')
          return
        }
        if (linkMode) {
          // Started from Settings > Security: link this Microsoft account to the signed-in user, then go back there.
          let notice = { ok: true, message: 'Microsoft account linked. You can now sign in with it.' }
          try {
            await authApiService.oauthLink('microsoft', idToken)
          } catch (e) {
            notice = { ok: false, message: e instanceof ApiError ? e.message : 'Linking the Microsoft account failed.' }
          }
          nav('/app/settings?tab=mfa', { replace: true, state: { signInMethodNotice: notice } })
          return
        }
        const signIn = await authApiService.oauthMicrosoft(idToken)
        if (signIn?.requiresMfa) { nav('/mfa'); return }
        if (signIn?.requiresEmailConfirmation) { nav('/sign-in/confirm', { replace: true, state: { email: signIn.email } }); return }
        // log('backend ok, refreshing')
        await ref()
        nav('/app/dashboard')
      } catch (e) {
        console.error('[MSAL] error:', e)
        if (linkMode) backToSettings('The Microsoft sign-in did not complete.')
        else if (e instanceof ApiError) nav('/login', { replace: true, state: { error: e.message } })
        else nav('/login?error=microsoft')
      }
    }).catch(e => {
      console.error('[MSAL] initialize error:', e)
      if (linkMode) backToSettings('The Microsoft sign-in did not complete.')
      else nav('/login?error=microsoft')
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}
